import { buildSourceScopedRefKey } from '@worldathletics/utilities';
import { getColorFromSpan } from '@/lib/text-color-ops';
import { getFontSizeFromSpan } from '@/lib/text-font-size-ops';

const REF_TOKEN_RE = /\{\{ref:([^}]+)\}\}/g;

const BLOCK_LINE_TAGS = new Set(['DIV', 'P']);

export const ALLOWED_TAGS = new Set([
  'B',
  'STRONG',
  'I',
  'EM',
  'U',
  'S',
  'STRIKE',
  'UL',
  'OL',
  'LI',
  'BR',
]);

// Tokens used by both serializeHtmlToText and sanitizeHtml. The renderer
// already inlines refMap into `{{ref:UUID}}` chips with .ref-chip; colored
// text persists as a literal `<span style="color:#hex">` because the chip
// pattern can't represent arbitrary spans.
function colorSpanWrap(colorHex: string, inner: string): string {
  return `<span style="color:${colorHex}">${inner}</span>`;
}

function fontSizeSpanWrap(pt: number, inner: string): string {
  return `<span style="font-size:${pt}pt">${inner}</span>`;
}

function isUnderlineOnlySpan(el: HTMLElement): boolean {
  if (el.tagName !== 'SPAN' || el.classList.contains('ref-chip')) {
    return false;
  }
  if (el.style.length === 0 || getColorFromSpan(el)) {
    return false;
  }
  for (let i = 0; i < el.style.length; i += 1) {
    const prop = el.style.item(i);
    if (prop !== 'text-decoration' && prop !== 'text-decoration-line') {
      return false;
    }
    const value = el.style.getPropertyValue(prop).toLowerCase();
    if (!value.includes('underline')) {
      return false;
    }
  }
  return true;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function isBlockLineElement(el: HTMLElement): boolean {
  return BLOCK_LINE_TAGS.has(el.tagName);
}

function isBlockLineBoundary(node: Node): boolean {
  return (
    node.nodeType === Node.ELEMENT_NODE &&
    isBlockLineElement(node as HTMLElement)
  );
}

function needsLineBreakBetween(prev: Node, curr: Node): boolean {
  return isBlockLineBoundary(prev) || isBlockLineBoundary(curr);
}

function emptyBlockLineOutput(el: HTMLElement): string | null {
  if (!isBlockLineElement(el)) return null;
  if (el.childNodes.length === 0) return '<br>';
  if (
    el.childNodes.length === 1 &&
    el.childNodes[0].nodeType === Node.ELEMENT_NODE &&
    (el.childNodes[0] as HTMLElement).tagName === 'BR'
  ) {
    return '<br>';
  }
  return null;
}

function joinChildOutputs(parent: Node, walk: (node: Node) => string): string {
  const children = Array.from(parent.childNodes);
  if (children.length === 0) return '';

  const parts = children.map(child => {
    const out = walk(child);
    if (out === '' && child.nodeType === Node.ELEMENT_NODE) {
      const emptyLine = emptyBlockLineOutput(child as HTMLElement);
      if (emptyLine) return emptyLine;
    }
    return out;
  });

  let result = parts[0] ?? '';
  for (let i = 1; i < children.length; i++) {
    if (needsLineBreakBetween(children[i - 1], children[i])) {
      result += '<br>';
    }
    result += parts[i];
  }
  return result;
}

/** All `{{ref:ID}}` token ids found in `text`, in first-occurrence order, deduped. */
export function extractRefTokenIds(text: string): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const match of text.matchAll(REF_TOKEN_RE)) {
    const id = match[1];
    if (id !== undefined && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}

export type ChipRefMeta = {
  name: string;
  kind?: 'internal' | 'url';
  status?: 'valid' | 'ignored';
  lost?: boolean;
};

/**
 * Looks up a chip token's metadata, preferring the entry scoped to this
 * specific source block over the plain (legacy, target-id-keyed) entry.
 * Legacy internal tokens splice the TARGET block's id into the text, so
 * without `sourceBlockId` a token shared by several reference rows (e.g.
 * several paragraphs all citing the same note) would always resolve to
 * whichever row `buildReferenceMetaMap` happened to process first — see
 * `buildSourceScopedRefKey`.
 */
export function resolveRefMeta<T extends ChipRefMeta>(
  refMap: Map<string, T>,
  tokenId: string,
  sourceBlockId?: string,
): T | undefined {
  if (sourceBlockId) {
    const scoped = refMap.get(buildSourceScopedRefKey(sourceBlockId, tokenId));
    if (scoped) return scoped;
  }
  return refMap.get(tokenId);
}

export function renderTextToHtml<T extends ChipRefMeta>(
  text: string,
  refMap: Map<string, T>,
  sourceBlockId?: string,
): string {
  return text.replace(REF_TOKEN_RE, (_, id: string) => {
    const meta = resolveRefMeta(refMap, id, sourceBlockId);
    // Ignored refs render as plain text — chip styling suppressed entirely.
    if (meta?.status === 'ignored') return escapeHtml(meta.name);
    const name = meta?.name ?? id;
    const brokenAttr =
      meta === undefined ? ' data-broken="true"' : '';
    const lostAttr = meta?.lost ? ' data-lost="true"' : '';
    const kindAttr = meta?.kind ? ` data-kind="${meta.kind}"` : '';
    const statusAttr = meta?.status ? ` data-status="${meta.status}"` : '';
    return `<span class="ref-chip"${brokenAttr}${lostAttr}${kindAttr}${statusAttr} contenteditable="false" data-ref-id="${escapeHtml(id)}">${escapeHtml(name)}</span>`;
  });
}

export function serializeHtmlToText(root: HTMLElement): string {
  const walk = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent ?? '';
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return '';
    }
    const el = node as HTMLElement;
    if (el.tagName === 'SPAN' && el.classList.contains('ref-chip')) {
      const id = el.getAttribute('data-ref-id') ?? '';
      return `{{ref:${id}}}`;
    }
    const tag = el.tagName.toLowerCase();
    const inner = joinChildOutputs(el, walk);
    // Single-color and single-font-size spans round-trip in canonical form.
    // Any other inline-style content on a span (background, …) is rejected
    // by both getters and the wrapper gets stripped below.
    if (el.tagName === 'SPAN') {
      const colorHex = getColorFromSpan(el);
      if (colorHex) return colorSpanWrap(colorHex, inner);
      const fontSizePt = getFontSizeFromSpan(el);
      if (fontSizePt !== null) return fontSizeSpanWrap(fontSizePt, inner);
    }
    if (isBlockLineElement(el)) {
      return inner;
    }
    if (ALLOWED_TAGS.has(el.tagName)) {
      if (tag === 'br') return '<br>';
      return `<${tag}>${inner}</${tag}>`;
    }
    return inner;
  };

  return joinChildOutputs(root, walk);
}

export function sanitizeHtml(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild as HTMLElement | null;
  if (!root) return '';

  const walk = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return escapeHtml(node.textContent ?? '');
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    const el = node as HTMLElement;
    const inner = joinChildOutputs(el, walk);

    if (
      el.tagName === 'SPAN' &&
      el.classList.contains('ref-chip') &&
      el.getAttribute('data-ref-id')
    ) {
      const id = el.getAttribute('data-ref-id') ?? '';
      return `<span class="ref-chip" contenteditable="false" data-ref-id="${escapeHtml(id)}">${inner}</span>`;
    }

    if (el.tagName === 'SPAN') {
      if (isUnderlineOnlySpan(el)) {
        return `<u>${inner}</u>`;
      }
      // Mirror serializeHtmlToText: preserve `<span style="color:#hex">` /
      // `<span style="font-size:Npt">` when (and only when) the inline style
      // declares exactly that one property.
      const colorHex = getColorFromSpan(el);
      if (colorHex) return colorSpanWrap(colorHex, inner);
      const fontSizePt = getFontSizeFromSpan(el);
      if (fontSizePt !== null) return fontSizeSpanWrap(fontSizePt, inner);
    }

    if (isBlockLineElement(el)) {
      return inner;
    }

    if (ALLOWED_TAGS.has(el.tagName)) {
      const tag = el.tagName.toLowerCase();
      if (tag === 'br') return '<br>';
      // Normalise b→strong and i→em on serialize so stored text matches the
      // renderer/view-sanitiser's canonical allowlist. <u> stays as <u>.
      const normalised = tag === 'b' ? 'strong' : tag === 'i' ? 'em' : tag;
      return `<${normalised}>${inner}</${normalised}>`;
    }

    return inner;
  };

  return joinChildOutputs(root, walk);
}

export function chipHtmlToTokens(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild as HTMLElement | null;
  if (!root) return '';
  return serializeHtmlToText(root);
}

/**
 * Replace the contents of a contenteditable node with the rendered HTML
 * produced by `renderTextToHtml(text, refMap)`. Used by EditableText to
 * repaint after Redux text changes from outside the component (e.g., when
 * the EditLink modal confirms a new reference and persists the token via
 * applyOps + refetch).
 *
 * Implementation uses Range#createContextualFragment instead of an
 * innerHTML assignment so the build doesn't trip the pre-Write security
 * heuristic. The output is functionally identical — the HTML is rendered
 * via the same allow-listed renderer and parsed in the node's own context.
 */
export function writeRenderedTextToNode<T extends ChipRefMeta>(
  node: HTMLElement,
  text: string,
  refMap: Map<string, T>,
  sourceBlockId?: string,
): void {
  const html = renderTextToHtml(text, refMap, sourceBlockId);
  const range = document.createRange();
  range.selectNodeContents(node);
  const fragment = range.createContextualFragment(html);
  node.replaceChildren(fragment);
}
